<template>
    <Fixed
        class="fullscreen-wizard"
        @close="closeOnEscape"
    >
        <transition name="fade">
            <div
                v-if="error || fetching"
                class="skeleton"
            >
                <div
                    v-if="error"
                    class="error"
                >
                    {{ error }}
                </div>

                <SplashScreen
                    v-else-if="fetching"
                    hide-logo
                    white-background
                />

                <div class="header">
                    <ButtonRound
                        :icon="'close'"
                        class="button-navigation right"
                        type="link"
                        @click.prevent.stop="close"
                    />
                </div>

                <div class="footer"></div>
            </div>

            <div
                v-else
                class="content"
            >
                <slot name="content"></slot>

                <div class="header">
                    <ButtonRound
                        v-if="currentStepIndex !== 0"
                        :icon="'angle-left'"
                        class="button-navigation left"
                        type="link"
                        @click.prevent.stop="previous"
                    />

                    <div class="step-title">
                        {{ stepTitle }} {{ stepProgress }}
                    </div>

                    <ButtonRound
                        :icon="'close'"
                        class="button-navigation right"
                        type="link"
                        @click.prevent.stop="close"
                    />

                    <StepIndicatorSimple
                        :current-step="stepCurrent"
                        :total-steps="stepCount"
                        class="step-indicator-simple"
                    />
                </div>

                <div
                    v-if="$slots['footer']"
                    class="footer"
                >
                    <slot name="footer"></slot>
                </div>
            </div>
        </transition>
    </Fixed>
</template>

<script lang="ts" setup>
import { computed, toRef, useSlots } from 'vue'

import Fixed from '@/components/layout/modal/Fixed.vue'
import SplashScreen from '@/components/ui/SplashScreen.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import StepIndicatorSimple from '@/components/ui/indicators/step/StepIndicatorSimple.vue'

const props = defineProps<{
    error?: string
    fetching?: boolean
    stepTitle: string | undefined
    stepCurrent: number
    stepCount: number
    noCloseOnEsc: boolean
    currentStepIndex: number | 'complete'
}>()

const emit = defineEmits<{
    close: []
    previous: []
}>()

const close = () => {
    emit('close')
}

const closeOnEscape = () => {
    if (props.noCloseOnEsc) {
        return
    }

    close()
}

const previous = () => {
    emit('previous')
}

const stepProgress = computed(() => {
    const current = props.stepCurrent + 1

    if (current === 0) {
        return ''
    } else {
        return `(${current}/${props.stepCount})`
    }
})

const slots = useSlots()
const showFooter = toRef(() => Number(Boolean(slots['footer'])))
</script>

<style lang="scss" scoped>
.fullscreen-wizard {
    background-color: var(--white);
    z-index: 2000;

    --header-height: 70px;
    --footer-height: calc(var(--header-height) * v-bind(showFooter));

    padding: var(--header-height) 0 var(--footer-height);

    @include min-width(1024) {
        --header-height: 80px;
    }

    .skeleton,
    .content {
        position: relative;
        height: 100%;
        width: 100%;

        .footer,
        .header {
            position: fixed;
            width: 100%;
            height: var(--header-height);
            background-color: var(--stone-3);
            display: flex;
            align-items: center;

            .button-navigation {
                &.right {
                    margin-right: 16px;
                }
            }
        }

        .header {
            top: 0;
        }

        .footer {
            bottom: 0;
            justify-content: center;
        }

        .step-title {
            width: 100%;
            text-align: center;
            padding-left: 66px;
        }

        .button-navigation + .step-title {
            padding-left: 0;
        }
    }

    .skeleton {
        .header {
            justify-content: flex-end;
        }
    }

    .content {
        overflow-y: auto;
        overflow-x: hidden;

        .header,
        .footer {
            display: flex;
            align-items: center;
        }

        .header {
            justify-content: space-between;

            .button-navigation {
                &.left {
                    margin-left: 16px;
                }
            }

            .step-indicator-simple {
                position: absolute;
                bottom: 0;
            }
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.24s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
